import { API_BASE_URL } from '../config';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Error codes for specific handling
export const API_ERROR_CODES = {
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  CLOCK_SKEW: 'CLOCK_SKEW',
  UNAUTHORIZED: 'UNAUTHORIZED',
} as const;

interface ApiClientOptions {
  token?: string;
  getToken?: () => Promise<string | undefined>;
}

export type ApiClient = ReturnType<typeof createApiClient>;

export const createApiClient = (tokenOrOptions?: string | ApiClientOptions) => {
  const options: ApiClientOptions = typeof tokenOrOptions === 'string'
    ? { token: tokenOrOptions }
    : tokenOrOptions || {};

  const makeRequest = async <T>(
    endpoint: string,
    requestOptions: RequestInit = {},
    isRetry = false
  ): Promise<T> => {
    // Get fresh token before each request
    const token = options.getToken ? await options.getToken() : options.token;

    if (!token) {
      throw new ApiError(401, 'No authentication token available');
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...requestOptions.headers,
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...requestOptions,
      headers,
    });

    if (!response.ok) {
      // Handle 401 with one retry after getting fresh token
      if (response.status === 401 && !isRetry && options.getToken) {
        return makeRequest<T>(endpoint, requestOptions, true);
      }

      // Parse error response for more details
      let errorCode: string | undefined;
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;

      try {
        const errorBody = await response.json();
        if (errorBody?.error?.message) {
          errorMessage = errorBody.error.message;

          // Detect clock skew / expired token issues
          if (response.status === 401 && errorMessage.includes('ExpiredSignature')) {
            errorCode = API_ERROR_CODES.CLOCK_SKEW;
            errorMessage = 'Session expired due to server time synchronization issue. Please contact your administrator. (Error: CLOCK_SKEW)';
          }
        }
      } catch {
        // Ignore JSON parse errors
      }

      throw new ApiError(response.status, errorMessage, errorCode);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  };

  return {
    fetch<T>(endpoint: string, requestOptions: RequestInit = {}): Promise<T> {
      return makeRequest<T>(endpoint, requestOptions);
    },

    get<T>(endpoint: string, options?: { signal?: AbortSignal }): Promise<T> {
      return this.fetch<T>(endpoint, { method: 'GET', signal: options?.signal });
    },

    post<T>(endpoint: string, body?: unknown, options?: { signal?: AbortSignal }): Promise<T> {
      return this.fetch<T>(endpoint, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
        signal: options?.signal,
      });
    },

    patch<T>(endpoint: string, body?: unknown, options?: { signal?: AbortSignal }): Promise<T> {
      return this.fetch<T>(endpoint, {
        method: 'PATCH',
        body: body ? JSON.stringify(body) : undefined,
        signal: options?.signal,
      });
    },

    delete<T>(endpoint: string, options?: { signal?: AbortSignal }): Promise<T> {
      return this.fetch<T>(endpoint, { method: 'DELETE', signal: options?.signal });
    },
  };
};
